import { DropDown, Pending } from '@finbeat/react-toolkit';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import { LIMIT_STATE_APPROVED, LIMIT_STATE_DECLINED_SCORE } from 'constans/limitStates';
import { PreOrderPaymentInfo } from 'models/api/merchant';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useMerchantOrderContext } from 'providers/MerchantOrderProvider';
import { Trans, useTranslation } from 'react-i18next';
import { UseQueryResult } from 'react-query';
import { Alert, Card, CardBody, CardHeader } from 'reactstrap';
import { useMerchantWorkplaceState } from '../Merchant';
import MerchantOrderContractFile from './MerchantOrderContractFile';

type MerchantOrderStatisticProps = {
    signedContractFileId: number | null;
    tariffGroups: DropDown[];
    contractFilesFetching: boolean;
    totalSum: number;
    totalCount: number;
    getPaymentQuery: UseQueryResult<PreOrderPaymentInfo, unknown>;
};

const MerchantOrderStatistic = ({
    signedContractFileId,
    tariffGroups,
    contractFilesFetching,
    totalSum,
    totalCount,
    getPaymentQuery,
}: MerchantOrderStatisticProps) => {
    const { dateFormat } = useGeneralSettingsContext();
    const { currentItem } = useMerchantWorkplaceState();
    const { t } = useTranslation();
    const { tariffGroupId, clientCreditLimit } = useMerchantOrderContext();

    const { isFetching, data } = getPaymentQuery;

    return (
        <Card className="mb-3">
            <CardHeader>
                <Trans i18nKey="BackOfficeInfo" />
            </CardHeader>
            <CardBody>
                <dl>
                    {currentItem && (
                        <>
                            <dt>
                                <Trans i18nKey="BackOfficeOrderStatus" />
                            </dt>
                            <dd>{currentItem?.statusName}</dd>
                            <dt>
                                <Trans i18nKey="BackOfficeOrderDate" />
                            </dt>
                            <dd>
                                <DataTimeText value={currentItem?.dt} />
                            </dd>
                        </>
                    )}
                    <dt>
                        <Trans i18nKey="BackOfficeProductTotalCount" />
                    </dt>
                    <dd>{totalCount}</dd>
                    <dt>
                        <Trans i18nKey="BackOfficeProductTotalSum" />
                    </dt>
                    <dd>
                        <Money value={totalSum} />
                    </dd>
                    <dt>
                        <Trans i18nKey="BackOfficeTariff" />
                    </dt>
                    <dd>
                        {(tariffGroupId &&
                            tariffGroups &&
                            (tariffGroups.find((g) => g.id === tariffGroupId)?.name ??
                                currentItem?.tariffName)) ||
                            '--'}
                    </dd>
                    <dt>
                        <Trans i18nKey="BackOfficePayment" />
                    </dt>
                    <dd>
                        {isFetching && <Pending />}
                        {!isFetching && data?.principalDebt !== null && (
                            <>
                                <Money value={data?.principalDebt} />
                                &nbsp;
                                <Trans i18nKey="BackOfficeMerchantSumPerMonth" />
                            </>
                        )}
                        {!isFetching && data?.principalDebt === null && '--'}
                    </dd>
                    <dt>
                        <Trans i18nKey="BackOfficeMerchantReceives" />
                    </dt>
                    <dd>
                        {isFetching && <Pending />}
                        {!isFetching && data?.merchantReceives !== null && (
                            <Money value={data?.merchantReceives} />
                        )}
                        {!isFetching && data?.merchantReceives === null && '--'}
                    </dd>
                    <dt>
                        <Trans i18nKey="BackOfficeInstallmentAmount" />
                    </dt>
                    <dd>
                        {isFetching && <Pending />}
                        {!isFetching && data?.adjustedSum !== null && (
                            <Money value={data?.adjustedSum} />
                        )}
                        {!isFetching && data?.adjustedSum === null && '--'}
                    </dd>
                    <dt>
                        <Trans i18nKey="BackOfficeLastPayment" />
                    </dt>
                    <dd>
                        {isFetching && <Pending />}
                        {!isFetching && data?.dt && (
                            <DataTimeText format={dateFormat} value={data?.dt} />
                        )}
                        {!isFetching && !data?.dt && '--'}
                    </dd>
                    {[LIMIT_STATE_APPROVED, LIMIT_STATE_DECLINED_SCORE].indexOf(
                        clientCreditLimit.limitState
                    ) >= 0 &&
                        getPaymentQuery.data &&
                        getPaymentQuery.data.adjustedSum > (clientCreditLimit.amount ?? 0) && (
                            <dd>
                                <Alert color="danger">
                                    <Trans i18nKey="BackOfficeMerchantOrderLimitError" />
                                </Alert>
                            </dd>
                        )}
                </dl>
                {signedContractFileId && (
                    <MerchantOrderContractFile
                        color="link"
                        contractFilesFetching={contractFilesFetching}
                        fileId={signedContractFileId}
                        btnName={t('BackOfficeMerchantContractSigned')}
                        fileType="contractSigned"
                    />
                )}
            </CardBody>
        </Card>
    );
};

export default MerchantOrderStatistic;
